import { Table } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/lib/table';
import { createUseStyles } from 'react-jss';
import { getColor } from '../../../pages/Simulation/utils';

const useStyles = createUseStyles({
  tableStyles: {
    '&& table > .ant-table-thead > tr > th': {
      fontWeight: 'bold',
    },
    '&& table > .ant-table-tbody > tr > td': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
    },
    // disable hover
    '& .ant-table-tbody > tr.ant-table-row:hover > td': {
      background: 'unset',
    },
  },
});

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const calculateRowMargin = (row: SellRateReportDatum) => row.simulationTotalSellRateCost - row.simulationTotalLabelCost;
const calculateRowMarginPercentage = (row: SellRateReportDatum) =>
  Math.round((calculateRowMargin(row) / row.simulationTotalLabelCost) * 10000) / 100;

export interface SellRateReportDatum {
  simulationTotalLabelCost: number;
  simulationTotalSellRateCost: number;
  simulationCount: number;
  sellRateZoneId?: string;
  carrierServiceMethodId?: string;
}

export interface SimulationReportingTableProps {
  reportAggregateField: 'sellRateZoneId' | 'carrierServiceMethodId';
  records: SellRateReportDatum[];
}

export const SimulationSellRateZoneTable = ({ records, reportAggregateField }: SimulationReportingTableProps) => {
  const classes = useStyles();

  const zoneColumn: ColumnType<SellRateReportDatum> = {
    title: 'Sell Rate Zone',
    key: 'sellRateZoneId',
    align: 'left',
    sorter: (a, b) => (a?.sellRateZoneId ?? '').localeCompare(b?.sellRateZoneId ?? ''),
    render: (val) => {
      return val.sellRateZoneId;
    },
  };
  const methodColumn: ColumnType<SellRateReportDatum> = {
    title: 'Selected Service Method',
    key: 'carrierServiceMethodId',
    align: 'left',
    sorter: (a, b) => (a?.carrierServiceMethodId ?? '').localeCompare(b?.carrierServiceMethodId ?? ''),
    render: (val) => {
      return val.carrierServiceMethodId;
    },
  };
  const IDcolumn = reportAggregateField === 'sellRateZoneId' ? zoneColumn : methodColumn;
  const columns: ColumnsType<SellRateReportDatum> = [
    IDcolumn,
    {
      title: 'Total Label Cost',
      key: 'simulationTotalLabelCost',
      sorter: (a, b) => (a.simulationTotalLabelCost ?? 0) - (b.simulationTotalLabelCost ?? 0),
      align: 'right',
      render: (val) => {
        return <span>{currencyFormatter.format(val.simulationTotalLabelCost)}</span>;
      },
    },
    {
      title: 'Sell Rate Price',
      key: 'simulationTotalSellRateCost',
      sorter: (a, b) => a.simulationTotalSellRateCost - b.simulationTotalSellRateCost,
      align: 'right',
      render: (val) => {
        return <span>{currencyFormatter.format(val.simulationTotalSellRateCost)}</span>;
      },
    },
    {
      title: 'Total Margin',
      key: 'simulationTotalSellRateMargin',
      sorter: (a, b) => calculateRowMargin(a) - calculateRowMargin(b),
      align: 'right',
      render: (val) => {
        const margin = calculateRowMargin(val);
        return <span style={{ color: getColor(margin) }}>{currencyFormatter.format(margin)}</span>;
      },
    },
    {
      title: 'Sell Margin Percentage',
      key: 'simulationlSellRateMarginPercentage',
      align: 'right',
      sorter: (a, b) => calculateRowMarginPercentage(a) - calculateRowMarginPercentage(b),
      render: (val) => {
        const marginPct = calculateRowMarginPercentage(val);
        return <span style={{ color: getColor(marginPct) }}>{marginPct}%</span>;
      },
    },
  ];
  return (
    <Table
      size={'small'}
      columns={columns}
      dataSource={records}
      pagination={false}
      bordered={false}
      className={classes.tableStyles}
    />
  );
};
